import { billingCopy } from './InvoiceFields';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createTranslator, useLanguage } from '../i18n/LanguageContext';

const copy = {
  fr: { loading: 'Chargement...', unavailable: 'Cet événement est indisponible.', title: 'Inscription individuelle', firstName: 'Prénom', lastName: 'Nom', email: 'E-mail', phone: 'Téléphone', company: 'Société / département (facultatif)', consent: "J’accepte que mes informations soient utilisées pour gérer mon inscription.", newsletter: 'Je souhaite recevoir la newsletter.', summary: 'Récapitulatif', date: 'Date', place: 'Lieu', price: 'Prix par personne', free: 'Gratuit', submit: "S'inscrire", redirecting: 'Redirection vers le paiement...', required: 'Complétez tous les champs obligatoires.', phoneError: 'Entrez un numéro de téléphone belge valide.', duplicate: 'Cette adresse e-mail est déjà inscrite.', full: 'Cet événement est complet.', error: 'Une erreur est survenue. Veuillez réessayer.' },
  de: { loading: 'Laden...', unavailable: 'Diese Veranstaltung ist nicht verfügbar.', title: 'Einzelanmeldung', firstName: 'Vorname', lastName: 'Nachname', email: 'E-Mail', phone: 'Telefon', company: 'Unternehmen / Abteilung (optional)', consent: 'Ich stimme der Verwendung meiner Daten zur Bearbeitung meiner Anmeldung zu.', newsletter: 'Ich möchte den Newsletter erhalten.', summary: 'Zusammenfassung', date: 'Datum', place: 'Ort', price: 'Preis pro Person', free: 'Kostenlos', submit: 'Anmelden', redirecting: 'Weiterleitung zur Zahlung...', required: 'Fülle alle Pflichtfelder aus.', phoneError: 'Gib eine gültige belgische Telefonnummer ein.', duplicate: 'Diese E-Mail-Adresse ist bereits angemeldet.', full: 'Diese Veranstaltung ist ausgebucht.', error: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.' },
  nl: { loading: 'Laden...', unavailable: 'Dit evenement is niet beschikbaar.', title: 'Individuele inschrijving', firstName: 'Voornaam', lastName: 'Achternaam', email: 'E-mail', phone: 'Telefoon', company: 'Bedrijf / afdeling (optioneel)', consent: 'Ik ga ermee akkoord dat mijn gegevens voor mijn inschrijving worden gebruikt.', newsletter: 'Ik wil de nieuwsbrief ontvangen.', summary: 'Overzicht', date: 'Datum', place: 'Locatie', price: 'Prijs per persoon', free: 'Gratis', submit: 'Inschrijven', redirecting: 'Doorsturen naar de betaling...', required: 'Vul alle verplichte velden in.', phoneError: 'Voer een geldig Belgisch telefoonnummer in.', duplicate: 'Dit e-mailadres is al ingeschreven.', full: 'Dit evenement is volzet.', error: 'Er is een fout opgetreden. Probeer opnieuw.' },
  en: { loading: 'Loading...', unavailable: 'This event is unavailable.', title: 'Individual registration', firstName: 'First name', lastName: 'Last name', email: 'Email', phone: 'Phone', company: 'Company / department (optional)', consent: 'I agree that my information may be used to manage my registration.', newsletter: 'I would like to receive the newsletter.', summary: 'Summary', date: 'Date', place: 'Location', price: 'Price per person', free: 'Free', submit: 'Register', redirecting: 'Redirecting to payment...', required: 'Complete all required fields.', phoneError: 'Enter a valid Belgian telephone number.', duplicate: 'This email address is already registered.', full: 'This event is full.', error: 'An error occurred. Please try again.' },
};

const API_BASE = 'https://us-central1-mendyx-adafe.cloudfunctions.net';



export default function TeamBuildingIndividualRegistration({ registrationEventId } = {}) {
  const { eventId: routeEventId } = useParams();
  const eventId = registrationEventId || routeEventId;
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = createTranslator(language, copy);
  const tb = createTranslator(language, billingCopy);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', company: '', consentInfo: false, newsletter: false, invoiceRequested: false, billingCompany: '', vatNumber: '', billingStreet: '', billingPostalCode: '', billingCity: '', billingCountry: '', billingEmail: '' });

  useEffect(() => {
    let active = true;
    fetch(`${API_BASE}/protectedGetTeamBuildingRegistrationPublicDetails?eventId=${encodeURIComponent(eventId)}`)
      .then(async (response) => {
        if (!response.ok) throw new Error('UNAVAILABLE');
        return response.json();
      })
      .then((data) => { if (active) setEvent(data.event); })
      .catch(() => { if (active) setError(copy[language]?.unavailable || copy.fr.unavailable); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [eventId, language]);

  const update = (eventChange) => {
    const { name, value, type, checked } = eventChange.target;
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const submit = async (submitEvent) => {
    submitEvent.preventDefault();
    setError('');
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.phone.trim() || !form.consentInfo) {
      setError(t('required')); return;
    }
    if (form.invoiceRequested && (!form.billingCompany.trim() || !form.vatNumber.trim() || !form.billingStreet.trim() || !form.billingPostalCode.trim() || !form.billingCity.trim() || !form.billingCountry.trim() || !form.billingEmail.trim())) {
      setError(tb('invoiceRequired')); return;
    }
    const compactPhone = form.phone.replace(/[\s./()-]/g, '');
    if (!/^(?:\+32|0)4\d{8}$/.test(compactPhone)) { setError(t('phoneError')); return; }

    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE}/protectedCreateTeamBuildingIndividualCheckout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId, ...form, phone: compactPhone, language }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.reason === 'duplicate') throw new Error('DUPLICATE');
        if (data.reason === 'full') throw new Error('FULL');
        throw new Error('GENERIC');
      }
      if (data.url) window.location.assign(data.url);
      else navigate('/success-team-building');
    } catch (submitError) {
      setError(submitError.message === 'DUPLICATE' ? t('duplicate') : submitError.message === 'FULL' ? t('full') : t('error'));
      setSubmitting(false);
    }
  };

  if (loading) return <main className="container mx-auto my-28 px-4 text-center">{t('loading')}</main>;
  if (!event) return <main className="container mx-auto my-28 px-4 text-center text-red-700">{error || t('unavailable')}</main>;

  const inputClass = 'mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-[#4c2f8e] focus:ring-4 focus:ring-[#4c2f8e]/10';
  const fields = [
    ['firstName', 'text', t('firstName')], ['lastName', 'text', t('lastName')],
    ['email', 'email', t('email')], ['phone', 'tel', t('phone')],
  ];
  const billingFields = [
    ['billingCompany', 'text'], ['vatNumber', 'text'], ['billingStreet', 'text'],
    ['billingPostalCode', 'text'], ['billingCity', 'text'], ['billingCountry', 'text'], ['billingEmail', 'email'],
  ];

  return (
    <main className="min-h-screen bg-[#f7f7fa] px-4 pb-16 pt-24">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#ea4c89]">Padel Mendyx</p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-[#24163f] md:text-5xl">{t('title')}</h1>
        </div>

        <div className="grid items-start gap-8 lg:grid-cols-[0.8fr_1.2fr]">
          <aside className="overflow-hidden rounded-3xl bg-[#24163f] text-white shadow-xl lg:sticky lg:top-24">
            {event.picture && <img src={event.picture} alt="" className="h-56 w-full object-cover" />}
            <div className="p-7 md:p-8">
              {event.company && <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#f2a7c5]">{event.company}</p>}
              <h2 className="mt-2 text-3xl font-black leading-tight">{event.name}</h2>
              {event.description && <p className="mt-4 leading-relaxed text-white/70">{event.description}</p>}
              <div className="mt-7 space-y-4 border-t border-white/15 pt-6 text-sm">
                <div><p className="text-white/50">{t('date')}</p><p className="mt-1 font-semibold">{event.date} · {event.time}</p></div>
                <div><p className="text-white/50">{t('place')}</p><p className="mt-1 font-semibold">{event.location}</p></div>
                <div><p className="text-white/50">{t('price')}</p><p className="mt-1 text-2xl font-black">{event.price > 0 ? `${event.price} €` : t('free')}</p></div>
              </div>
              {event.price > 0 && <p className="mt-6 rounded-xl bg-white/10 px-4 py-3 text-center text-xs font-semibold text-white/80">🔒 {tb('secure')}</p>}
            </div>
          </aside>

          <form onSubmit={submit} className="rounded-3xl border border-gray-100 bg-white p-6 shadow-xl shadow-gray-200/60 md:p-10">
            <section>
              <h2 className="text-2xl font-black text-[#24163f]">{tb('details')}</h2>
              <p className="mt-1 text-sm text-gray-500">{tb('detailsHint')}</p>
              <div className="mt-6 grid gap-5 md:grid-cols-2">
                {fields.map(([name, type, label]) => (
                  <label key={name} className="text-sm font-semibold text-gray-700">{label} *
                    <input name={name} type={type} value={form[name]} onChange={update} className={inputClass} required />
                  </label>
                ))}
                <label className="text-sm font-semibold text-gray-700 md:col-span-2">{t('company')}
                  <input name="company" value={form.company} onChange={update} className={inputClass} />
                </label>
              </div>
            </section>

            <section className="mt-8 rounded-2xl border border-gray-200 bg-gray-50 p-5">
              <label className="flex cursor-pointer items-start gap-4">
                <input name="invoiceRequested" type="checkbox" checked={form.invoiceRequested} onChange={update} className="mt-1 h-5 w-5 accent-[#4c2f8e]" />
                <span><span className="block font-bold text-[#24163f]">{tb('invoice')}</span><span className="mt-1 block text-sm text-gray-500">{tb('invoiceHint')}</span></span>
              </label>

              {form.invoiceRequested && (
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <h3 className="font-bold text-[#24163f]">{tb('billing')}</h3>
                  <div className="mt-4 grid gap-5 md:grid-cols-2">
                    {billingFields.map(([name, type]) => (
                      <label key={name} className={`text-sm font-semibold text-gray-700 ${name === 'billingStreet' || name === 'billingEmail' ? 'md:col-span-2' : ''}`}>
                        {tb(name)} *
                        <input name={name} type={type} value={form[name]} onChange={update} className={inputClass} required={form.invoiceRequested} />
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className="mt-7 space-y-4 border-t border-gray-100 pt-7">
              <label className="flex items-start gap-3 text-sm text-gray-600"><input name="consentInfo" type="checkbox" checked={form.consentInfo} onChange={update} className="mt-0.5 h-4 w-4 accent-[#4c2f8e]" required />{t('consent')} *</label>
              <label className="flex items-start gap-3 text-sm text-gray-600"><input name="newsletter" type="checkbox" checked={form.newsletter} onChange={update} className="mt-0.5 h-4 w-4 accent-[#4c2f8e]" />{t('newsletter')}</label>
              <p className="text-xs text-gray-400">{tb('requiredHint')}</p>
            </section>

            {error && <p className="mt-6 rounded-xl border border-red-100 bg-red-50 p-4 text-sm font-medium text-red-700">{error}</p>}
            <button disabled={submitting || event.registrationDisabled} className="mt-7 w-full rounded-xl bg-[#4c2f8e] px-6 py-4 text-base font-bold text-white shadow-lg shadow-[#4c2f8e]/20 transition hover:-translate-y-0.5 hover:bg-[#3a226d] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0">
              {submitting ? t('redirecting') : t('submit')}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
